import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Text } from 'src/@vymo/ui/atoms';
import TextArea from 'src/@vymo/ui/atoms/textArea';
import { ReactComponent as CheckIcon } from 'src/assets/icons/check.svg';
import { ReactComponent as SendSvg } from 'src/assets/icons/chevron_up.svg';
import { ReactComponent as CloseButton } from 'src/assets/icons/close.svg';
import { ReactComponent as MicButton } from 'src/assets/icons/mic.svg';
import { useSpeechRecognition } from 'src/hooks/useSpeechRecognition';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getUploadedFile } from './selector';
import { sendMessage } from './thunk';
import { CopilotVersion } from './types';
import { sendMessage as sendWorkflowMessage } from './workflow/thunk';
import styles from './index.module.scss';

function Footer({
  version,
  chatSessionId,
  context,
}: {
  version?: CopilotVersion;
  chatSessionId: string;
  context?: Record<string, any>;
}) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [typedMessage, setTypedMessage] = useState<string>('');
  const dispatch = useAppDispatch();
  const uploadedFile = useAppSelector(getUploadedFile);
  const {
    isRecording,
    toggleRecording,
    transcript,
    isSpeechSupported,
    audioLevel,
  } = useSpeechRecognition();

  useEffect(() => {
    setTypedMessage(`${typedMessage} ${transcript}`.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  const handleSend = useCallback(() => {
    if (typedMessage) {
      setTypedMessage('');
      switch (version) {
        case 'workflow':
          dispatch(
            sendWorkflowMessage({
              text: typedMessage,
              file: uploadedFile,
              chatSessionId,
              context,
            }),
          );
          break;
        case 'default':
        default:
          dispatch(
            sendMessage({ text: typedMessage, file: uploadedFile, context }),
          );
          break;
      }
    }
  }, [chatSessionId, context, dispatch, typedMessage, uploadedFile, version]);

  const onPressKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const keyType = event.key;
      if (
        ['Enter'].includes(keyType) &&
        !event.shiftKey &&
        typedMessage?.trim()
      ) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend, typedMessage],
  );

  const handleTextChange = useCallback((text) => {
    setTypedMessage(text);
    if (textAreaRef?.current) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, []);

  return (
    <div className={styles.copilotv2__footer}>
      <TextArea
        classNames={styles.copilotv2__footer__textArea}
        ref={textAreaRef}
        value={typedMessage}
        onChange={handleTextChange}
        onKeyDown={onPressKeyDown}
        footer={
          <div className={styles.copilotv2__footer__textArea__actions}>
            {isRecording && (
              <div className={styles.copilotv2__footer__audioLevel}>
                {Array.from({ length: 100 }).map((i) => (
                  <span
                    key={i as any}
                    style={{
                      height: `${2 + audioLevel * 160}px`,
                    }}
                  />
                ))}
              </div>
            )}
            {/* {<Button
              onClick={() => {}}
              type="text"
              rounded
              isIcon
              size="small"
              disabled
              className={styles.copilotv2__footer__button}
              iconProps={{
                icon: <AddButton />,
                iconPosition: 'left',
              }}
            />} */}
            <Button
              onClick={() => toggleRecording(true)}
              type="text"
              rounded
              isIcon
              disabled={!isSpeechSupported}
              size="small"
              className={styles.copilotv2__footer__button}
              iconProps={{
                icon: isRecording ? <CloseButton /> : <MicButton />,
                iconPosition: 'left',
              }}
            />{' '}
            {!isRecording ? (
              <Button
                onClick={handleSend}
                type="primary"
                rounded
                isIcon
                size="small"
                variant={!typedMessage ? 'subtle' : 'filled'}
                disabled={!typedMessage?.trim()}
                className={styles.copilotv2__footer__button}
                iconProps={{
                  icon: <SendSvg />,
                  iconPosition: 'left',
                }}
              />
            ) : (
              <Button
                onClick={() => toggleRecording()}
                type="text"
                rounded
                isIcon
                size="small"
                className={styles.copilotv2__footer__button}
                iconProps={{
                  icon: <CheckIcon />,
                  iconPosition: 'left',
                }}
              />
            )}
          </div>
        }
        placeholder="Ask anything..."
      />
      <Text classNames={styles.copilotv2__footer__text} type="sublabel">
        Uses Ai, check results
      </Text>
    </div>
  );
}

export default Footer;
